/**
 * AuthCallback – handles Emergent Google OAuth redirect.
 *
 * Emergent returns the user to /auth/callback#session_id=<id>. We exchange
 * that session_id on the backend for our own JWT cookie pair, then bounce
 * to /home (or /onboarding if the user is new).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SpinnerGap } from '@phosphor-icons/react';
import { http, apiErrorMessage, setStoredToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState('');
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : '';

    if (!sessionId) {
      setError('Missing session id');
      return;
    }

    (async () => {
      try {
        const { data } = await http.post('/auth/google/session', { session_id: sessionId });
        if (data.access_token) setStoredToken(data.access_token);
        setUser(data);
        // Clean the hash out of the URL
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate(data.onboarding_status === 'complete' ? '/home' : '/onboarding', { replace: true });
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="lf-shell flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-[var(--lf-destructive)] font-semibold mb-3" data-testid="auth-callback-error">{error}</div>
            <button onClick={() => navigate('/login')} className="lf-btn lf-btn-primary">
              Back to sign in
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 text-[var(--lf-text-muted)]" data-testid="auth-callback-loading">
            <SpinnerGap size={32} className="animate-spin text-[var(--lf-primary)]" />
            Signing you in…
          </div>
        )}
      </div>
    </div>
  );
}
